export type ErrorCategory =
  | 'rate_limit'
  | 'provider_quota'
  | 'content_blocked'
  | 'timeout'
  | 'auth'
  | 'network'
  | 'schema_validation'
  | 'url_fetch_blocked'
  | 'tool_failed'
  | 'gemini_signature'
  | 'server_error'
  | 'insufficient_credits'
  | 'context_length_exceeded'
  | 'unknown';

export interface FriendlyError {
  title: string;
  description: string;
  action: 'retry' | 'report' | 'reauth' | 'none';
  retryable: boolean;
}

export const FRIENDLY_ERRORS: Record<ErrorCategory, FriendlyError> = {
  rate_limit: {
    title: 'Too many requests',
    description: 'Give it a moment and try again.',
    action: 'retry',
    retryable: true,
  },
  /**
   * The AI provider refused the request because MORA's account is out of
   * quota or billing (Gemini 429 RESOURCE_EXHAUSTED "exceeded your current
   * quota, please check your plan and billing details"). Distinct from
   * rate_limit: waiting a moment does not help, and the user did nothing to
   * cause it. 2026-10-21 17:03-17:36 UTC every chat turn failed this way and
   * was shown "The AI took too long. Try again with a simpler request."
   */
  provider_quota: {
    title: 'Mora cannot reach its AI service right now',
    description:
      'The AI provider has paused requests for Mora. This is on our side, not yours, and our team has been notified.',
    action: 'none',
    retryable: false,
  },
  content_blocked: {
    title: 'Content flagged by safety filter',
    description: 'Try rephrasing your request.',
    action: 'retry',
    retryable: true,
  },
  timeout: {
    title: 'Request timed out',
    description: 'The AI took too long. Try again with a simpler request.',
    action: 'retry',
    retryable: true,
  },
  auth: {
    title: 'Session expired',
    description: 'Sign in again to continue.',
    action: 'reauth',
    retryable: false,
  },
  network: {
    title: 'Connection lost',
    description: 'Check your internet and try again.',
    action: 'retry',
    retryable: true,
  },
  schema_validation: {
    title: 'Unexpected AI response',
    description: 'The AI returned something we could not process. Trying again usually fixes it.',
    action: 'retry',
    retryable: true,
  },
  url_fetch_blocked: {
    title: "Couldn't open that link",
    description:
      "The site refused the fetch or blocked bots. Try a different URL, or describe your brand in a sentence and I'll use that instead.",
    action: 'retry',
    retryable: true,
  },
  tool_failed: {
    title: 'Step failed mid-flight',
    description:
      'The AI tool reported an error partway through. Your other tools completed fine — retry the step that failed.',
    action: 'retry',
    retryable: true,
  },
  gemini_signature: {
    title: 'AI provider hiccup',
    description: 'Gemini lost the reasoning chain between tool calls. Retrying normally fixes it.',
    action: 'retry',
    retryable: true,
  },
  server_error: {
    title: 'Server error',
    description: 'We hit a snag on our end — try again in a moment.',
    action: 'retry',
    retryable: true,
  },
  /**
   * Running out of credits is the most PREDICTABLE thing this product does, and
   * it had no entry here — so it fell through to `unknown` and the user was told
   * "Something unexpected happened. Our team has been notified." directly above
   * a line reading "This needs 4 credits; 1 available."
   *
   * One state, two systems describing it, flatly contradicting each other — and
   * the alarming, wrong one on top. Nothing was broken, nobody was notified, and
   * there was nothing to report; the account simply needs more credits, which is
   * a purchase, not an incident.
   *
   * `action: 'none'` and `retryable: false` because retrying cannot succeed:
   * offering Retry on a balance of 1 against a cost of 4 is a button that is
   * guaranteed to fail. The CTA belongs to `InsufficientCreditsCard` instead.
   */
  insufficient_credits: {
    title: 'Not enough credits',
    description: 'Add credits or move to a plan with more, and this will run right away.',
    action: 'none',
    retryable: false,
  },
  /**
   * A long conversation is a full inbox, not a broken one — the same voice as
   * `insufficient_credits` above: tell the user what to DO, not just what
   * broke. Retrying the same turn cannot possibly succeed (the whole thread
   * gets resent every turn, so it hits the same wall again), so this follows
   * the same `action: 'none'` / `retryable: false` pattern rather than
   * offering a Retry button that is guaranteed to fail.
   */
  context_length_exceeded: {
    title: 'This conversation has gotten long',
    description:
      "This chat has grown past what the AI can read in one go. Start a new chat, or branch from an earlier point in this one, and it'll pick right back up.",
    action: 'none',
    retryable: false,
  },
  unknown: {
    title: 'Something unexpected happened',
    description: 'Our team has been notified. Try again or report if it persists.',
    action: 'report',
    retryable: true,
  },
};

/**
 * Categorize a raw error string into a stable ErrorCategory. Exported so a
 * server/edge route can map once, return the friendly description AND a
 * structured `errorCode`, and downstream views render from the code instead of
 * re-mapping an already-friendly string (mapRawErrorToFriendly is NOT idempotent
 * — mapping a friendly description would degrade it to `unknown`).
 */
/**
 * Provider-side quota or billing exhaustion, as opposed to a transient rate
 * limit. Both arrive as HTTP 429; only the wording tells them apart.
 */
export function isProviderQuotaMessage(lowerCaseMessage: string): boolean {
  return (
    lowerCaseMessage.includes('resource_exhausted') ||
    lowerCaseMessage.includes('exceeded your current quota') ||
    (lowerCaseMessage.includes('quota') && lowerCaseMessage.includes('billing'))
  );
}

export function categorizeRawError(rawError: string | null | undefined): ErrorCategory {
  if (!rawError) return 'unknown';
  const msg = rawError.toLowerCase();

  // Checked FIRST, before the rate-limit branch. That branch matches on the bare
  // word "quota", which a credits message can legitimately contain — and once it
  // matched, an empty wallet was reported to the user as "Too many requests,
  // give it a moment and try again", advice that can never work.
  if (
    msg.includes('insufficient_credits') ||
    msg.includes('insufficient credits') ||
    msg.includes('not enough credits')
  ) {
    return 'insufficient_credits';
  }

  // Checked BEFORE both the rate-limit and timeout branches below, for the
  // same reason insufficient_credits is checked before rate-limit: a real
  // context-overflow message can legitimately contain "exceeded" (which the
  // timeout branch already claims) and, in principle, "quota"-adjacent
  // phrasing (which the rate-limit branch claims on the bare word "quota").
  // No exact Gemini/Vertex "input too large" error string exists anywhere in
  // the installed @ai-sdk/google, @ai-sdk/google-vertex, ai, or @google/genai
  // packages (checked node_modules directly — it's a runtime API response
  // value, never a literal in SDK source), so this matches only on our own
  // errorCode from the pre-flight check in server/api/agent/index.post.ts
  // (`errorCode: 'context_length_exceeded'`, matched by the raw string of the
  // code itself) plus a conservative compound heuristic that requires
  // "token"/"context" co-occurring with "exceed"/"maximum" — deliberately
  // narrower than a single bare keyword so it cannot be a coincidental hit.
  if (
    msg.includes('context_length_exceeded') ||
    msg.includes('context length exceeded') ||
    (msg.includes('context window') && (msg.includes('exceed') || msg.includes('maximum'))) ||
    (msg.includes('token') && msg.includes('context') && msg.includes('exceed'))
  ) {
    return 'context_length_exceeded';
  }

  if (isProviderQuotaMessage(msg)) {
    return 'provider_quota';
  }
  if (
    msg.includes('429') ||
    msg.includes('too many requests') ||
    msg.includes('quota') ||
    msg.includes('rate limit')
  ) {
    return 'rate_limit';
  }
  if (msg.includes('timeout') || msg.includes('abortsignal') || msg.includes('exceeded')) {
    return 'timeout';
  }
  // Checked BEFORE the 400/403 tool_failed branch below: a message that names
  // an auth-specific cause (expired session, invalid credentials) is more
  // useful surfaced as "sign in again" than the generic tool_failed copy. A
  // bare 401/403 with no auth-specific wording still falls through to
  // tool_failed unchanged — this only wins on unambiguous auth signals.
  if (
    msg.includes('401') ||
    msg.includes('unauthorized') ||
    msg.includes('unauthenticated') ||
    msg.includes('jwt expired') ||
    msg.includes('invalid api key') ||
    msg.includes('authentication failed') ||
    msg.includes('session expired')
  ) {
    return 'auth';
  }
  if (
    msg.includes('400') ||
    msg.includes('403') ||
    msg.includes('invalid_argument') ||
    msg.includes('failed_precondition')
  ) {
    return 'tool_failed';
  }
  /**
   * 🔴 CHECKED BEFORE `content_blocked`, which matches the BARE WORD "block".
   *
   * `url_fetch_blocked` was declared with dedicated copy and no branch here ever
   * returned it — structurally dead, exactly like `auth` and `network` were
   * before the 2026-08-24 audit noted below. The live consequence, measured
   * 2026-09-15 on a real `scan_article` failure: the error "That site blocks
   * scrapers — paste the article text instead?" was classified
   * `content_blocked`, whose user-facing copy is "Content flagged by safety
   * filter. Try rephrasing your request." Nothing was flagged and there was
   * nothing to rephrase — the page simply would not load. Rephrasing a prompt
   * cannot fix a URL that does not resolve.
   *
   * `handle-error.ts:86-94` already had this detection; this function did not,
   * so the two classifiers disagreed about the same failure. Now they agree.
   *
   * This matters beyond the toast: `agent_tool_receipts.error_category` records
   * this value per tool call, so a wrong category here becomes a wrong
   * diagnosis on the admin tool-failure panel — a confident measurement of the
   * wrong thing.
   */
  if (
    msg.includes("couldn't open") ||
    msg.includes("couldn't read that page") ||
    msg.includes('blocks scrapers') ||
    msg.includes('refused the fetch') ||
    msg.includes('fetch blocked') ||
    msg.includes('refused to connect') ||
    msg.includes('robots.txt') ||
    msg.includes('ssrf') ||
    msg.includes('paste the article text') ||
    msg.includes('paste the text instead')
  ) {
    return 'url_fetch_blocked';
  }

  if (
    msg.includes('safety') ||
    msg.includes('block') ||
    msg.includes('policy') ||
    msg.includes('prohibited')
  ) {
    return 'content_blocked';
  }
  // 'auth' and 'network' were declared in FRIENDLY_ERRORS with dedicated
  // copy but no branch here ever returned either — both were structurally
  // dead code (verified 2026-08-24 audit), which is why a real network
  // blip calling Vertex/FAL, or fetching a reference image, fell through to
  // the generic 'unknown' "our team has been notified" copy instead of the
  // accurate, actionable "check your internet and try again". This matches
  // the observed errorCode:'unknown' spike (11 occurrences, 2026-07-21).
  if (
    msg.includes('econnrefused') ||
    msg.includes('econnreset') ||
    msg.includes('enotfound') ||
    msg.includes('etimedout') ||
    msg.includes('socket hang up') ||
    msg.includes('network error') ||
    msg.includes('fetch failed') ||
    msg.includes('failed to fetch') ||
    msg.includes('getaddrinfo') ||
    msg.includes('dns')
  ) {
    return 'network';
  }

  // fallback unknown
  return 'unknown';
}

export function mapRawErrorToFriendly(rawError: string | null | undefined): FriendlyError {
  return FRIENDLY_ERRORS[categorizeRawError(rawError)];
}

/**
 * Friendly, user-safe display string for a caught error — for toast surfaces.
 *
 * Precedence:
 *   1. A structured `errorCode` that maps to a known category → that category's
 *      copy. The server maps a raw provider dump to a category code before it
 *      leaves the route, so a Vertex 404 / model-not-found string is turned into
 *      friendly copy here and never reaches the toast (incident 2026-07-21).
 *   2. Any other error — a structured-but-uncategorized server error (e.g.
 *      `daily_image_cap`) or a code-less local/app throw (e.g. "No image
 *      returned.") — already carries an app-authored, user-safe message, so it
 *      is surfaced verbatim rather than degraded to the generic 'unknown' copy.
 *
 * Always log the raw detail via console.error alongside the call — this returns
 * only what the user should see.
 */
export function friendlyMessageFromError(err: unknown): string {
  const code = (err as { errorCode?: unknown } | null | undefined)?.errorCode;
  if (typeof code === 'string' && code in FRIENDLY_ERRORS) {
    return FRIENDLY_ERRORS[code as ErrorCategory].description;
  }
  const raw = err instanceof Error ? err.message : typeof err === 'string' ? err : '';
  return raw || FRIENDLY_ERRORS.unknown.description;
}

/**
 * `friendlyMessageFromError` plus the server's own retry hint.
 *
 * The 429 path already computes `retryAfter` server-side; without this the
 * client discarded it and showed the generic "give it a moment" copy, so the
 * user had no idea whether "a moment" was 2 seconds or 2 minutes.
 */
export function friendlyMessageWithRetry(err: unknown): string {
  const retryAfter = (err as { retryAfter?: unknown } | null | undefined)?.retryAfter;
  const code = (err as { errorCode?: unknown } | null | undefined)?.errorCode;
  if (code === 'rate_limit' && typeof retryAfter === 'number' && retryAfter > 0) {
    return `Too many edits — try again in ${Math.ceil(retryAfter)}s.`;
  }
  return friendlyMessageFromError(err);
}

/**
 * Plain copy for a raw provider error stored on a connected account
 * (`connections.last_error`). Says what happened and what to do; the raw text
 * (for example "(#200) ... publish_actions ... deprecated") stays in logs and
 * the integration detail view, never in a tooltip or status line.
 */
export function friendlyConnectionErrorMessage(rawError: string | null | undefined): string {
  const msg = (rawError ?? '').toLowerCase();
  if (
    msg.includes('publish_actions') ||
    msg.includes('(#200)') ||
    msg.includes('permission') ||
    msg.includes('oauthexception')
  ) {
    return 'This account no longer allows Mora to post. Reconnect it to grant access again.';
  }
  if (categorizeRawError(rawError) === 'rate_limit') {
    return 'This account is limiting requests right now. Mora will try again later.';
  }
  return 'This account reported a problem. Try reconnecting it; if that does not help, contact support.';
}

const BROWSER_NETWORK_MESSAGE =
  '(?:Failed to fetch|NetworkError when attempting to fetch resource\\.?|Load failed|fetch failed)';
const BROWSER_ERROR_CLASS = '(?:Type|Network)Error:?\\s*';
const WHOLE_BROWSER_NETWORK_ERROR = new RegExp(
  `^\\s*(?:${BROWSER_ERROR_CLASS})?${BROWSER_NETWORK_MESSAGE}\\s*$`,
  'i',
);
const TRAILING_BROWSER_NETWORK_ERROR = new RegExp(
  `(?:\\s*:\\s*|\\s+(?=(?:Type|Network)Error))(?:${BROWSER_ERROR_CLASS})?${BROWSER_NETWORK_MESSAGE}\\s*$`,
  'i',
);

const LEADING_BROWSER_NETWORK_ERROR = new RegExp(
  `^\\s*(?:[A-Za-z ]{1,40}:\\s*){0,3}(?:${BROWSER_ERROR_CLASS})?${BROWSER_NETWORK_MESSAGE}(?=\\s*[.;,]|\\s+\\S)`,
  'i',
);
const STORAGE_PATH_SUFFIX = /\s*\.?\s*Path:\s*\S+\s*$/i;

export const NETWORK_TOAST_DESCRIPTION = 'Check your connection and try again.';

export interface PlainToastText {
  message: string;
  description?: string;
}

function tidyToastText(text: string): string {
  return text
    .replace(/\s+/g, ' ')
    .replace(/\s*[:\-–—]\s*$/, '')
    .replace(/^\s*[:\-–—]\s*/, '')
    .trim();
}

export function plainToastText(message: string, description?: string): PlainToastText {
  let networkFailure = false;
  const strip = (text: string): string => {
    if (WHOLE_BROWSER_NETWORK_ERROR.test(text)) {
      networkFailure = true;
      return '';
    }
    if (LEADING_BROWSER_NETWORK_ERROR.test(text)) {
      networkFailure = true;
      return '';
    }
    return text
      .replace(TRAILING_BROWSER_NETWORK_ERROR, () => {
        networkFailure = true;
        return '';
      })
      .replace(STORAGE_PATH_SUFFIX, '');
  };

  const title = tidyToastText(strip(message)) || message;
  let detail = description === undefined ? undefined : tidyToastText(strip(description));

  if (detail) {
    const lowerTitle = title.toLowerCase();
    const lowerDetail = detail.toLowerCase();
    if (lowerDetail === lowerTitle) {
      detail = undefined;
    } else if (lowerDetail.startsWith(`${lowerTitle}:`)) {
      detail = tidyToastText(detail.slice(title.length + 1));
    }
  }

  if (networkFailure && !detail) detail = NETWORK_TOAST_DESCRIPTION;
  if (!detail) detail = undefined;

  return { message: title, description: detail };
}
